import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export const CONSENT_STORAGE_KEY = "inkstead_cookie_consent";
const CONSENT_VERSION = 1;

const REJECTED = { essential: true, analytics: false, marketing: false };

// Returns a valid saved record or null (missing, malformed, outdated, or storage unavailable).
export function readConsent() {
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (
      !data || typeof data !== "object" || data.version !== CONSENT_VERSION ||
      data.status !== "set" || !data.categories || typeof data.categories !== "object" ||
      typeof data.timestamp !== "string" || Number.isNaN(Date.parse(data.timestamp))
    ) return null;
    return {
      ...data,
      categories: {
        essential: true,
        analytics: data.categories.analytics === true,
        marketing: data.categories.marketing === true,
      },
    };
  } catch {
    return null;
  }
}

function writeConsent(categories) {
  const record = {
    version: CONSENT_VERSION,
    status: "set",
    categories: { essential: true, analytics: !!categories.analytics, marketing: !!categories.marketing },
    timestamp: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
  } catch {
    /* storage unavailable: consent applies for this session only */
  }
  return record;
}

/**
 * Third-party script gating. Register loaders here; they run ONLY after the matching
 * category is granted. No analytics/marketing vendor is currently integrated in the app,
 * so all lists are empty. Example:
 *   analytics: [() => loadScript("https://www.googletagmanager.com/gtag/js?id=G-XXXX")]
 * A script cannot be un-run once loaded; put vendor opt-out calls in `revokers`
 * (e.g. window["ga-disable-G-XXXX"] = true). Full removal takes effect on next page load.
 */
const loaders = { analytics: [], marketing: [] };
const revokers = { analytics: [], marketing: [] };
const started = new Set();

export function loadScript(src, attrs = {}) {
  if (document.querySelector(`script[src="${src}"]`)) return;
  const s = document.createElement("script");
  s.src = src;
  s.async = true;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
}

function applyConsent(categories) {
  ["analytics", "marketing"].forEach((cat) => {
    if (categories[cat]) {
      if (!started.has(cat)) {
        started.add(cat);
        loaders[cat].forEach((fn) => { try { fn(); } catch { /* ignore vendor errors */ } });
      }
    } else if (started.has(cat)) {
      started.delete(cat);
      revokers[cat].forEach((fn) => { try { fn(); } catch { /* ignore vendor errors */ } });
    }
  });
}

const ConsentContext = createContext(null);

export function ConsentProvider({ children }) {
  const [consent, setConsent] = useState(() => readConsent());
  const [prefsOpen, setPrefsOpen] = useState(false);

  useEffect(() => {
    applyConsent(consent ? consent.categories : REJECTED);
  }, [consent]);

  const save = useCallback((categories) => {
    setConsent(writeConsent(categories));
    setPrefsOpen(false);
  }, []);

  const value = useMemo(() => ({
    consent,
    hasDecided: !!consent,
    categories: consent ? consent.categories : REJECTED,
    prefsOpen,
    openPreferences: () => setPrefsOpen(true),
    closePreferences: () => setPrefsOpen(false),
    acceptAll: () => save({ analytics: true, marketing: true }),
    rejectOptional: () => save(REJECTED),
    savePreferences: save,
  }), [consent, prefsOpen, save]);

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error("useConsent must be used within ConsentProvider");
  return ctx;
}
